import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import PhoneInput from "../components/PhoneInput";
import { DEFAULT_COUNTRY } from "../data/countries";

export default function RegisterPage() {
  const { register } = useAuth();
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    phone: { country: DEFAULT_COUNTRY.code, dial: DEFAULT_COUNTRY.dial, number: "" },
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      // Compose E.164-style phone string for storage
      const phoneDigits = form.phone.number.replace(/\s+/g, "");
      if (phoneDigits.length < 5) {
        setErr("Telefon nömrəsi düzgün deyil.");
        setLoading(false);
        return;
      }
      const phoneFull = `${form.phone.dial}${phoneDigits}`;
      await register({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        phone: phoneFull,
        phone_country: form.phone.country,
        phone_dial: form.phone.dial,
        phone_number: phoneDigits,
      });
      nav("/profile", { replace: true });
    } catch (ex) {
      const code = ex?.code || "";
      const map = {
        "auth/email-already-in-use": "Bu email artıq istifadə olunur.",
        "auth/invalid-email": "Email formatı düzgün deyil.",
        "auth/weak-password": "Parol ən az 6 simvol olmalıdır.",
      };
      setErr(map[code] || ex.message || "Qeydiyyat alınmadı.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="metadata mb-3">{t.common.account}</div>
        <h1 className="section-title text-5xl">{t.common.register}</h1>
        <form onSubmit={onSubmit} className="mt-10 space-y-4">
          <input className="w-full bg-transparent border border-[var(--border)] px-4 py-3 outline-none focus:border-[var(--primary)] font-mono text-base"
                 placeholder="full name" value={form.full_name}
                 onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                 required data-testid="reg-name" />
          <input className="w-full bg-transparent border border-[var(--border)] px-4 py-3 outline-none focus:border-[var(--primary)] font-mono text-base"
                 placeholder="email" value={form.email}
                 onChange={(e) => setForm({ ...form, email: e.target.value })}
                 type="email" required data-testid="reg-email" />
          <PhoneInput
            value={form.phone}
            onChange={(p) => setForm({ ...form, phone: p })}
            required
            testid="reg-phone"
            lang={lang}
            placeholder="50 123 45 67"
          />
          <div className="relative">
            <input className="w-full bg-transparent border border-[var(--border)] pl-4 pr-12 py-3 outline-none focus:border-[var(--primary)] font-mono text-base"
                   placeholder="password (min 6)" value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })}
                   type={showPwd ? "text" : "password"} required data-testid="reg-password" />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Parolu gizlət" : "Parolu göstər"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-2)] hover:text-[var(--text)] transition"
              data-testid="reg-toggle-password"
            >
              {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {err && <div className="text-[var(--accent)] text-sm">{err}</div>}
          <button disabled={loading} className="btn-primary w-full justify-center" data-testid="reg-submit">
            {loading ? "…" : t.common.register}
          </button>
        </form>
        <div className="mt-6 metadata">
          {t.common.alreadyMember} <Link to="/login" className="link-underline text-[var(--primary)]">{t.common.login}</Link>
        </div>
      </div>
    </div>
  );
}
